import { Button } from "@package/subframe/components/Button";

import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";

/**
 * Confirmation modal shown before recording a "Log Check" entry on a log.
 *
 * Shared across the Logs v1 tickets that gate check submission behind an
 * explicit practitioner confirmation:
 *   - NAU-190: Sleep Check confirmation
 *   - NAU-96:  Garden Supervision confirmation
 *   - NAU-191: Sleep Check + "Actions Taken" (extends this modal with an
 *              optional free-text field and a second CTA — to be added via
 *              new props when that ticket lands; do not pre-build here)
 *
 * The caller owns closing the modal after `onConfirm` resolves so the future
 * NAU-191 secondary CTA can keep it open during the navigation transition.
 */
interface LogCheckConfirmationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  confirmationText: string[];
  onConfirm: () => Promise<void> | void;
  primaryCtaLabel?: string;
  isSubmitting?: boolean;
}

export function LogCheckConfirmationModal({
  open,
  onOpenChange,
  title,
  confirmationText,
  onConfirm,
  primaryCtaLabel = "Confirm Check",
  isSubmitting = false,
}: LogCheckConfirmationModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent showCloseButton={false} className="max-w-[480px] p-0">
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-white">
          <div className="flex max-w-[448px] flex-col items-center gap-6 p-4">
            <div className="flex flex-col items-center gap-3">
              <span className="w-full text-center text-heading-2 font-heading-2 text-default-font">
                {title}
              </span>
              <div className="flex max-h-[50vh] flex-col gap-3 overflow-y-auto">
                {confirmationText.map((paragraph, index) => (
                  <p
                    key={index}
                    className="text-center text-body font-body text-subtext-color"
                  >
                    {paragraph}
                  </p>
                ))}
              </div>
            </div>
          </div>
          <div className="flex w-full flex-col items-center gap-3 bg-white px-4 py-4">
            <Button
              className="h-14 w-80 flex-none"
              variant="neutral-secondary"
              size="extra-large"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              className="h-14 w-80 flex-none"
              variant="brand-primary"
              size="extra-large"
              onClick={onConfirm}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Confirming..." : primaryCtaLabel}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
